import { useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { CARENCIAS, PRAZOS_DE_RECEBIMENTO, TABELAS_DE_PRECO, TIPOS_DE_PLANO, problemaDoPlano } from '@/data/planosDeSaude';
import type { DadosDoPlanoDeSaude, PlanoDeSaude, TipoDePlano } from '@/data/planosDeSaude';
import { SeletorDeCor } from '@/screens/configuracoes/SeletorDeCor';

/** A lista fixa, mais o valor gravado quando ele não está nela: editar não troca em silêncio. */
function opcoesCom(lista: string[], atual: string) {
    return [...new Set([...lista, ...(atual ? [atual] : [])])].map((v) => ({ value: v, label: v }));
}

export interface PlanoModalProps {
    alvo: PlanoDeSaude | 'novo';
    onFechar: () => void;
    onSalvo: () => void;
}

/**
 * Cadastro e edição do plano. Os campos mudam com o tipo: convênio pede
 * registro na ANS e carência; cartão de desconto e particular, não.
 */
export function PlanoModal({ alvo, onFechar, onSalvo }: PlanoModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idTipo = useId();
    const idOperadora = useId();
    const idRegistro = useId();
    const idCarencia = useId();
    const idTabela = useId();
    const idRepasse = useId();
    const idPrazo = useId();
    const idObservacoes = useId();
    const novo = alvo === 'novo';

    /* Os padrões do novo são os do protótipo: convênio, 70% de repasse, CBHPM. */
    const inicial = {
        nome: novo ? '' : alvo.nome,
        tipo: (novo ? 'Convênio' : alvo.tipo) as TipoDePlano,
        operadora: novo ? '' : alvo.operadora ?? '',
        registroAns: novo ? '' : alvo.registroAns ?? '',
        carencia: novo ? '90 dias' : alvo.carencia ?? '',
        tabelaDePrecos: novo ? 'CBHPM 2024' : alvo.tabelaDePrecos ?? '',
        repasse: novo ? '70' : alvo.repassePercentual === undefined ? '' : String(alvo.repassePercentual).replace('.', ','),
        prazoRecebimento: novo ? '45 dias' : alvo.prazoRecebimento ?? '',
        cor: novo ? '#2986FF' : alvo.cor,
        observacoes: novo ? '' : alvo.observacoes ?? '',
        ativo: novo ? true : alvo.ativo,
    };
    const [campos, setCampos] = useState(inicial);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);
    const mudar = <C extends keyof typeof campos>(campo: C, valor: (typeof campos)[C]) => {
        setCampos((c) => ({ ...c, [campo]: valor }));
        setErro(null);
    };

    const convenio = campos.tipo === 'Convênio';
    const repasseTexto = campos.repasse.trim();
    const repasse = repasseTexto === '' ? undefined : Number(repasseTexto.replace(',', '.'));
    const repasseInvalido = repasse !== undefined && !(Number.isFinite(repasse) && repasse >= 0 && repasse <= 100);
    const registroInvalido = convenio && campos.registroAns.trim() !== '' && !/^\d{6}$/.test(campos.registroAns.trim());

    const dadosDoPlano: DadosDoPlanoDeSaude = {
        nome: campos.nome, tipo: campos.tipo, cor: campos.cor, ativo: campos.ativo,
        operadora: campos.operadora, registroAns: campos.registroAns, carencia: campos.carencia,
        tabelaDePrecos: campos.tabelaDePrecos, prazoRecebimento: campos.prazoRecebimento, observacoes: campos.observacoes,
        ...(repasse !== undefined && !repasseInvalido ? { repassePercentual: repasse } : {}),
    };
    const problema = repasseInvalido ? 'O repasse vai de 0 a 100%.' : problemaDoPlano(dadosDoPlano);

    const salvar = async () => {
        if (problema) return;
        setSalvando(true);
        try {
            await dados().salvarPlanoDeSaude(novo ? null : alvo.id, dadosDoPlano);
            aviso({
                title: novo ? 'Plano cadastrado' : 'Plano atualizado',
                description: novo ? `${campos.nome.trim()} · já aparece nas opções da ficha do paciente` : campos.nome.trim(),
            });
            onSalvo();
            onFechar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar o plano.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={620} onClose={onFechar}
            title={novo ? 'Cadastrar plano de saúde' : 'Editar plano'}
            description={novo ? 'Depois de salvo, o plano aparece nas opções da ficha do paciente.' : alvo.nome}
            dirty={JSON.stringify(campos) !== JSON.stringify(inicial)} onSave={() => { void salvar(); }}
            saveLabel={novo ? 'Cadastrar plano' : 'Salvar'}
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={Boolean(problema) || salvando}>
                        {salvando ? 'Salvando…' : novo ? 'Cadastrar plano' : 'Salvar'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 14 }}>
                {erro && <div style={{ gridColumn: '1 / -1' }}><Alert tone="danger" title="Não foi possível salvar" dismissible={false}>{erro}</Alert></div>}

                <FormField label="Nome do plano" htmlFor={idNome} required style={{ gridColumn: '1 / -1' }}>
                    <Input id={idNome} value={campos.nome} onChange={(v) => mudar('nome', v)} placeholder="Ex.: Unimed Nacional" />
                </FormField>
                <FormField label="Tipo" htmlFor={idTipo} required>
                    <Select id={idTipo} value={campos.tipo} onChange={(v) => mudar('tipo', v as TipoDePlano)} options={TIPOS_DE_PLANO.map((t) => ({ value: t, label: t }))} />
                </FormField>
                <FormField label="Operadora" htmlFor={idOperadora}>
                    <Input id={idOperadora} value={campos.operadora} onChange={(v) => mudar('operadora', v)} placeholder="Ex.: Unimed" />
                </FormField>

                {convenio && (
                    <>
                        <FormField label="Registro na ANS" htmlFor={idRegistro} hint="Seis dígitos, sem pontuação" error={registroInvalido ? 'O registro na ANS tem seis dígitos' : undefined}>
                            <Input id={idRegistro} value={campos.registroAns} onChange={(v) => mudar('registroAns', v)} inputMode="numeric" placeholder="339679" invalid={registroInvalido} />
                        </FormField>
                        <FormField label="Carência" htmlFor={idCarencia}>
                            <Select
                                id={idCarencia} value={campos.carencia} onChange={(v) => mudar('carencia', v)}
                                options={[...CARENCIAS.map((c) => ({ value: c.valor, label: c.rotulo })), ...(campos.carencia && !CARENCIAS.some((c) => c.valor === campos.carencia) ? [{ value: campos.carencia, label: campos.carencia }] : [])]}
                            />
                        </FormField>
                    </>
                )}

                <FormField label="Tabela de preços" htmlFor={idTabela}>
                    <Select id={idTabela} value={campos.tabelaDePrecos} onChange={(v) => mudar('tabelaDePrecos', v)} placeholder="Sem tabela" options={opcoesCom(TABELAS_DE_PRECO, campos.tabelaDePrecos)} />
                </FormField>
                <FormField label="Repasse" htmlFor={idRepasse} hint="Percentual do valor de tabela que a clínica recebe" error={repasseInvalido ? 'De 0 a 100' : undefined}>
                    <Input id={idRepasse} value={campos.repasse} onChange={(v) => mudar('repasse', v)} inputMode="decimal" suffix="%" invalid={repasseInvalido} />
                </FormField>
                <FormField label="Prazo de pagamento" htmlFor={idPrazo}>
                    <Select id={idPrazo} value={campos.prazoRecebimento} onChange={(v) => mudar('prazoRecebimento', v)} placeholder="Sem prazo definido" options={opcoesCom(PRAZOS_DE_RECEBIMENTO, campos.prazoRecebimento)} />
                </FormField>

                <FormField
                    label="Cor de identificação" style={{ gridColumn: '1 / -1' }}
                    hint="Usada na etiqueta do paciente. A paleta evita tons de vinho, reservados ao acento da marca."
                >
                    <SeletorDeCor valor={campos.cor} onChange={(v) => mudar('cor', v)} rotulo="Cor do plano" />
                </FormField>

                <FormField label="Observações" htmlFor={idObservacoes} style={{ gridColumn: '1 / -1' }}>
                    <Textarea
                        id={idObservacoes} rows={2} value={campos.observacoes} onChange={(v) => mudar('observacoes', v)}
                        placeholder="Regras de autorização, limite de sessões, contato do faturamento…"
                    />
                </FormField>
                <Switch
                    checked={campos.ativo} onChange={(v) => mudar('ativo', v)} label="Plano ativo" style={{ gridColumn: '1 / -1' }}
                    description="Inativo sai das opções da ficha do paciente e continua no cadastro."
                />
            </div>
        </Modal>
    );
}
